import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import Loader from '../pages/loader'
import { can } from '../../helpers/auth'
import { toast } from 'react-toastify'
import ReusableTable from '../../components/other/ReuseableTable'
import {
  useDeleteCampaignAdvancedKeywordMutation,
  useGetCampaignAdvancedKeywordsQuery,
  useLazyGetCampaignAdvancedKeywordsQuery,
  useUpdateCampaignAdvancedKeywordMutation
} from '../../redux/apis/campaignsManagement/advancedKeywords/cMAdvancedKeywordsApiSlice'
import AddSearchEngineKeywordModal from '../../components/campaigns/searchEngineKeyword/AddSearchEngineKeywordModal'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import LanguageDropdown from '../../components/LanguageDropdown'
import CampaignDropdown from '../../components/CampaignDropdown'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'

const CampaignsSearchEngineKeywords = React.memo(() => {
  const context = useContext(AppSettings)

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})

  const { data, isLoading, error, isFetching } =
    useGetCampaignAdvancedKeywordsQuery({
      expand: ['last_updated_by', 'campaign', 'language'].join(','),
      page: pagination.current,
      page_size: pagination.pageSize,
      ...(search ? { search } : {}),
      ...filters
    })

  const [remove, { isLoading: deleteIsLoading, error: removeError }] =
    useDeleteCampaignAdvancedKeywordMutation()

  const [updateStatus, { isLoading: updateIsLoading }] =
    useUpdateCampaignAdvancedKeywordMutation()

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'CAMPAIGN ADVANCED KEYWORD',
      dataIndex: 'campaign_advanced_keyword',
      key: 'campaign_advanced_keyword',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'CAMPAIGN NAME',
      dataIndex: 'campaign',
      key: 'campaign.campaign_name',
      filter: true,
      filterComponent: CampaignDropdown,
      render: text => <p>{text?.campaign_name}</p>
    },
    {
      title: 'KEYWORD STATUS',
      dataIndex: 'keyword_status',
      key: 'keyword_status',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
      ],
      render: text => <p>{text ? 'Active' : 'Inactive'}</p>
    },
    {
      title: 'LANGUAGE',
      dataIndex: 'language',
      key: 'language.language_name',
      filter: true,
      filterComponent: LanguageDropdown,
      render: text => <p>{text?.language_name}</p>
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'CREATED AT',
      dataIndex: 'created_at',
      key: 'created_at',

    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (_text, record) => (
        <div className='flex gap-2'>
          {can('add_search_engine_keywords_campaign') && (
            record?.keyword_status ? (
              <button
                className='btn btn-outline-danger w-full'
                onClick={() => remove(record?.id)}
              >
                Deactivate
              </button>
            ) : (
              <button
                className='btn btn-theme w-full'
                onClick={() =>
                  updateStatus({ id: record?.id, body: { keyword_status: true } })
                }
              >
                Activate
              </button>
            )
          )}
        </div>
      )
    }
  ]

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(msg => toast.error(msg))
    }
  }, [error])

  useEffect(() => {
    if (removeError?.data) {
      const errorMessages = Object.values(removeError.data).flat()
      errorMessages.forEach(msg => toast.error(msg))
    }
  }, [removeError])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader
        isLoading={
          isLoading || deleteIsLoading || updateIsLoading || isFetching
        }
      />

      <div className='flex mb-4'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetCampaignAdvancedKeywordsQuery}
            fileName='campaigns-search-engine-keywords'
          />
          {can('add_search_engine_keywords_campaign') && <AddSearchEngineKeywordModal />}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default CampaignsSearchEngineKeywords
